/**
 * Meta Ads connect card — unified across onboarding + integrations.
 *
 * File name kept as FacebookAdsCard.tsx for internal consistency; all
 * user-facing copy says "Meta Ads" per POSITIONING.
 *
 * Phase 5 refactor (2026-05-30): same shape as GoogleAdsCard — Skipped
 * branch deleted, checkbox always visible in not-connected state, new
 * showFireCounts prop for integrations chrome.
 */

import { useState } from 'react';
import { CheckCircle2, Info } from 'lucide-react';

import { useAuth } from '@/lib/useAuth';
import { apiRequest } from '@/lib/api';
import { platformLogo } from '@/lib/attributionDisplay';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface FacebookAdsCardProps {
  /** Checkbox value. Reflects `me.facebook_skipped_at` for integrations,
   *  or parent's local willSkip state for onboarding's deferred commit. */
  willSkip?: boolean;
  /** Called when user toggles the checkbox. Onboarding sets local state;
   *  integrations should POST /onboarding/skip immediately. */
  onWillSkipChange?: (next: boolean) => void;
  /** Integrations chrome: today + last-30 conversions fired strip in the
   *  connected state. */
  showFireCounts?: boolean;
  firesToday?: number;
  firesLast30d?: number;
}

export function FacebookAdsCard({
  willSkip = false,
  onWillSkipChange,
  showFireCounts = false,
  firesToday,
  firesLast30d,
}: FacebookAdsCardProps = {}) {
  const { me } = useAuth();
  const [busy, setBusy] = useState<'connect' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pixelId = me?.facebook_pixel_id ?? null;

  async function startConnect() {
    setBusy('connect');
    setError(null);
    try {
      const res = await apiRequest<{ auth_url: string }>(
        '/oauth/facebook/start',
      );
      window.location.href = res.auth_url;
    } catch {
      setError('Could not start the Meta Ads connection. Please try again.');
      setBusy(null);
    }
  }

  if (pixelId) {
    return (
      <div className="rounded-3xl border border-green-200 bg-card p-7 shadow-card sm:p-9">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-700" />
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-bold text-dark">Meta Ads connected</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Dataset ID: <code className="font-mono">{pixelId}</code>
            </p>
            <button
              type="button"
              onClick={startConnect}
              disabled={busy === 'connect'}
              className="mt-2 text-xs font-medium text-green-800 underline underline-offset-2 hover:no-underline disabled:opacity-60"
            >
              Reconnect
            </button>
            {showFireCounts && (
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span>
                  <span className="font-bold text-dark">{firesToday ?? 0}</span>{' '}
                  <span className="text-muted-foreground">fired today</span>
                </span>
                <span className="text-dark/20">·</span>
                <span>
                  <span className="font-bold text-dark">
                    {(firesLast30d ?? 0).toLocaleString()}
                  </span>{' '}
                  <span className="text-muted-foreground">last 30 days</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-card p-7 shadow-card sm:p-9">
      <div className="flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-muted">
          <img src={platformLogo('meta')} alt="" className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold text-dark">Connect Meta Ads</h3>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">
            We send Lead, Qualified, and Purchase events to your Meta dataset
            via the Conversions API. Meta optimizes for real customers, not just
            clicks.
          </p>
          <div className="mt-5">
            <Banner variant="warn" size="compact" icon={Info}>
              On the next screen Meta asks for several permissions.{' '}
              <strong>Approve Pixel and Ad Account access.</strong> Pages is
              optional.
            </Banner>
          </div>
          {error && (
            <div className="mt-5">
              <Banner variant="error" size="compact">
                {error}
              </Banner>
            </div>
          )}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              onClick={startConnect}
              disabled={busy !== null}
              className="rounded-full bg-dark px-6 py-3 font-bold text-white hover:bg-dark/90"
            >
              {busy === 'connect' ? 'Opening Meta…' : 'Connect Meta Ads'}
            </Button>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={willSkip}
                disabled={busy !== null}
                onCheckedChange={(checked) =>
                  onWillSkipChange?.(checked === true)
                }
              />
              <span>I don't run Meta Ads</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
